import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Lock, Trash2, Palette, Loader2, AlertTriangle, Shield, Monitor, Clock, LogOut, X, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface LoginEntry {
  id: string;
  logged_in_at: string;
  device_type: string | null;
  browser: string | null;
  os: string | null;
}

interface SecurityAlert {
  id: string;
  title: string;
  message: string;
  alert_type: string;
  is_read: boolean;
  created_at: string;
  metadata: any;
}

const Settings = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [reauthCode, setReauthCode] = useState('');
  const [isSendingReauthCode, setIsSendingReauthCode] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');
  const [isRevokingSessions, setIsRevokingSessions] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'dark';
  });

  // 2FA state
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FALoading, setIs2FALoading] = useState(true);
  const [isToggling2FA, setIsToggling2FA] = useState(false);
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [twoFAOtp, setTwoFAOtp] = useState('');
  const [pending2FAAction, setPending2FAAction] = useState<'enable' | 'disable' | null>(null);
  const [isSending2FAOtp, setIsSending2FAOtp] = useState(false);

  // Security alerts state
  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);

  // Login history state
  const [loginHistory, setLoginHistory] = useState<LoginEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    if (user) {
      load2FAStatus();
      loadLoginHistory();
      loadSecurityAlerts();
      recordLogin();
    }
  }, [user]);

  const loadSecurityAlerts = async () => {
    try {
      const { data } = await supabase
        .from('security_alerts' as any)
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (data) setSecurityAlerts(data as any);
    } catch { /* ignore */ }
  };

  const dismissAlert = async (id: string) => {
    await supabase.from('security_alerts' as any).update({ is_read: true }).eq('id', id);
    setSecurityAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const dismissAllAlerts = async () => {
    const ids = securityAlerts.filter((a) => !a.is_read).map((a) => a.id);
    if (ids.length > 0) {
      for (const id of ids) {
        await supabase.from('security_alerts' as any).update({ is_read: true }).eq('id', id);
      }
    }
    setSecurityAlerts([]);
  };

  const load2FAStatus = async () => {
    setIs2FALoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('two-factor-auth', {
        body: { action: 'check' },
      });
      if (!error && data) setIs2FAEnabled(data.enabled);
    } catch { /* ignore */ }
    setIs2FALoading(false);
  };

  const toggle2FA = async () => {
    // Step 1: Send OTP for verification before toggling
    const action = is2FAEnabled ? 'disable' : 'enable';
    setPending2FAAction(action);
    setIsSending2FAOtp(true);
    try {
      const { error } = await supabase.auth.reauthenticate();
      if (error) {
        toast.error('Failed to send verification code');
        setPending2FAAction(null);
        return;
      }
      setShow2FAVerify(true);
      setTwoFAOtp('');
      toast.info('Enter the verification code sent to your email');
    } catch {
      toast.error('Failed to send verification code');
      setPending2FAAction(null);
    } finally {
      setIsSending2FAOtp(false);
    }
  };

  const confirm2FAToggle = async () => {
    if (twoFAOtp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setIsToggling2FA(true);
    try {
      // Verify the OTP nonce
      const { error: verifyError } = await supabase.auth.updateUser({
        data: { last_2fa_toggle: new Date().toISOString() },
        nonce: twoFAOtp,
      });
      if (verifyError) {
        toast.error('Invalid or expired code. Please try again.');
        setTwoFAOtp('');
        setIsToggling2FA(false);
        return;
      }

      // OTP verified — now toggle 2FA
      const { error } = await supabase.functions.invoke('two-factor-auth', {
        body: { action: pending2FAAction },
      });
      if (error) throw error;
      setIs2FAEnabled(pending2FAAction === 'enable');
      toast.success(pending2FAAction === 'enable' 
        ? '2FA enabled — a code will be sent to your email on login' 
        : '2FA disabled');
      setShow2FAVerify(false);
      setTwoFAOtp('');
      setPending2FAAction(null);
    } catch (error: any) {
      toast.error('Failed to update 2FA settings');
    }
    setIsToggling2FA(false);
  };

  const cancel2FAVerify = () => {
    setShow2FAVerify(false);
    setTwoFAOtp('');
    setPending2FAAction(null);
  };

  const loadLoginHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { data } = await supabase
        .from('login_history' as any)
        .select('id, logged_in_at, device_type, browser, os')
        .eq('user_id', user!.id)
        .order('logged_in_at', { ascending: false })
        .limit(10);
      if (data) setLoginHistory(data as any);
    } catch { /* ignore */ }
    setIsLoadingHistory(false);
  };

  const recordLogin = async () => {
    const ua = navigator.userAgent;
    const browser = getBrowser(ua);
    const os = getOS(ua);
    const deviceType = /Mobile|Android|iPhone/i.test(ua) ? 'Mobile' : 'Desktop';

    // Only record if last login was > 30 min ago
    const { data: recent } = await supabase
      .from('login_history' as any)
      .select('id')
      .eq('user_id', user!.id)
      .gte('logged_in_at', new Date(Date.now() - 30 * 60 * 1000).toISOString())
      .limit(1);

    if (!recent || recent.length === 0) {
      // Check for new device & create security alert
      try {
        const { data: alertData } = await supabase.functions.invoke('login-alert', {
          body: { action: 'check_and_alert', browser, os, device_type: deviceType },
        });
        if (alertData?.is_new_device) {
          toast.warning('🔐 New device login detected! Check Security Alerts above.');
          loadSecurityAlerts();
        }
      } catch { /* ignore */ }

      await supabase.from('login_history' as any).insert({
        user_id: user!.id,
        user_agent: ua,
        browser,
        os,
        device_type: deviceType,
      });
    }
  };

  const handleRevokeAllSessions = async () => {
    setIsRevokingSessions(true);
    try {
      const { data, error } = await supabase.functions.invoke('login-alert', {
        body: { action: 'revoke_all_sessions' },
      });
      if (error) throw error;
      toast.success('All other sessions have been revoked');
      loadLoginHistory();
    } catch (error: any) {
      toast.error('Failed to revoke sessions');
    }
    setIsRevokingSessions(false);
  };

  const handleDeleteLoginEntry = async (id: string) => {
    try {
      await supabase.from('login_history' as any).delete().eq('id', id);
      setLoginHistory((prev) => prev.filter((e) => e.id !== id));
      toast.success('Login entry removed');
    } catch {
      toast.error('Failed to remove entry');
    }
  };

  const getBrowser = (ua: string): string => {
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Unknown';
  };

  const getOS = (ua: string): string => {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown';
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        toast.error('Current password is incorrect');
        return;
      }

      if (!reauthCode) {
        setIsSendingReauthCode(true);
        const { error: reauthError } = await supabase.auth.reauthenticate();
        if (reauthError) {
          toast.error('Failed to send verification code');
          return;
        }

        toast.info('Verification code sent to your email. Enter it to confirm password change.');
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword, nonce: reauthCode });
      if (error) throw error;

      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setReauthCode('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
      setIsSendingReauthCode(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeletePasswordError('Please enter your password to confirm');
      return;
    }
    setDeletePasswordError('');
    setIsDeletingAccount(true);
    try {
      // Verify password first
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: deletePassword,
      });
      if (signInError) {
        setDeletePasswordError('Incorrect password');
        setIsDeletingAccount(false);
        return;
      }
      if (user) {
        await supabase.from('profiles').delete().eq('id', user.id);
        await supabase.from('temp_mail_history').delete().eq('user_id', user.id);
      }
      await signOut();
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
      setDeletePassword('');
    }
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
    if (value === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
    localStorage.setItem('theme', value);
    toast.success(`Theme changed to ${value} mode`);
  };

  const getDeviceIcon = (type: string | null) => {
    return type === 'Mobile' ? '📱' : '🖥️';
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <SettingsIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your account settings</p>
        </div>
      </div>

      {/* Security Alerts */}
      {securityAlerts.filter((a) => !a.is_read).length > 0 && (
        <Card className="glass border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-destructive text-base">
                <AlertTriangle className="h-5 w-5" />
                Security Alerts ({securityAlerts.filter((a) => !a.is_read).length})
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={dismissAllAlerts}>
                Dismiss all
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {securityAlerts.filter((a) => !a.is_read).map((alert) => (
              <div
                key={alert.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20"
              >
                <Shield className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{alert.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {format(new Date(alert.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => dismissAlert(alert.id)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Theme Settings */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme Preference
          </CardTitle>
          <CardDescription>Choose your preferred color scheme</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={theme} onValueChange={handleThemeChange} className="grid grid-cols-2 gap-4">
            <div>
              <RadioGroupItem value="dark" id="dark" className="peer sr-only" />
              <Label
                htmlFor="dark"
                className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer transition-all"
              >
                <div className="w-full h-20 rounded bg-[#0a0f1c] mb-2 border" />
                <span className="font-medium">Dark</span>
              </Label>
            </div>
            <div>
              <RadioGroupItem value="light" id="light" className="peer sr-only" />
              <Label
                htmlFor="light"
                className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer transition-all"
              >
                <div className="w-full h-20 rounded bg-slate-100 mb-2 border" />
                <span className="font-medium">Light</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-500" />
            Two-Factor Authentication
            {is2FAEnabled && (
              <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">Active</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Add an extra layer of security. A verification code will be sent to your email each time you log in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-accent/20 border border-border/50">
            <div className="space-y-1">
              <p className="text-sm font-medium">Email OTP Verification</p>
              <p className="text-xs text-muted-foreground">
                {is2FAEnabled
                  ? 'A 6-digit code is sent to your email on every login'
                  : 'Enable to receive a verification code via email on login'}
              </p>
            </div>
            {is2FALoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={is2FAEnabled}
                onCheckedChange={toggle2FA}
                disabled={isToggling2FA || isSending2FAOtp || show2FAVerify}
              />
            )}
          </div>

          {/* OTP Verification for 2FA toggle */}
          {show2FAVerify && (
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4 text-primary" />
                Enter verification code to {pending2FAAction} 2FA
              </div>
              <p className="text-xs text-muted-foreground">
                A 6-digit code was sent to <span className="font-medium text-foreground">{user?.email}</span>
              </p>
              <div className="flex gap-2">
                <Input
                  value={twoFAOtp}
                  onChange={(e) => setTwoFAOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="font-mono text-center tracking-widest max-w-[160px] h-10"
                  maxLength={6}
                />
                <Button
                  onClick={confirm2FAToggle}
                  disabled={isToggling2FA || twoFAOtp.length !== 6}
                  size="sm"
                  className="h-10"
                >
                  {isToggling2FA ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancel2FAVerify}
                  className="h-10"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Login History & Session Management */}
      <Card className="glass border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Login History & Sessions
              </CardTitle>
              <CardDescription>Recent login sessions — revoke access from unfamiliar devices</CardDescription>
            </div>
            {loginHistory.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={handleRevokeAllSessions}
                disabled={isRevokingSessions}
              >
                {isRevokingSessions ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                Revoke All Sessions
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : loginHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No login history yet</p>
          ) : (
            <div className="space-y-3">
              {loginHistory.map((entry, index) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-accent/20 border border-border/30 transition-all hover:bg-accent/30 group"
                >
                  <span className="text-xl">{getDeviceIcon(entry.device_type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      {entry.browser || 'Unknown'} on {entry.os || 'Unknown'}
                      {index === 0 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-500/50 text-green-500">
                          Current
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {entry.device_type || 'Unknown device'} • {format(new Date(entry.logged_in_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  {index !== 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteLoginEntry(entry.id)}
                      title="Remove this entry"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <p className="text-xs text-muted-foreground">
              For sensitive changes, we send a one-time verification code to your email.
            </p>
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reauth-code">Verification Code</Label>
              <Input
                id="reauth-code"
                type="text"
                value={reauthCode}
                onChange={(e) => setReauthCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code from email"
                inputMode="numeric"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={isChangingPassword || isSendingReauthCode} className="gap-2">
                {isChangingPassword ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                {reauthCode ? 'Confirm Password Change' : 'Send Verification Code'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isChangingPassword || isSendingReauthCode}
                onClick={async () => {
                  setIsSendingReauthCode(true);
                  const { error } = await supabase.auth.reauthenticate();
                  if (error) {
                    toast.error('Failed to resend verification code');
                  } else {
                    toast.success('Verification code resent');
                  }
                  setIsSendingReauthCode(false);
                }}
              >
                {isSendingReauthCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend Code'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Delete Account */}
      <Card className="glass border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>Irreversible actions</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2">
                <Trash2 className="h-4 w-4" />
                Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Delete Account
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. Enter your password to permanently delete your account and all data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="delete-password" className="text-sm font-medium">Confirm Password</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => { setDeletePassword(e.target.value); setDeletePasswordError(''); }}
                  placeholder="Enter your password"
                />
                {deletePasswordError && (
                  <p className="text-xs text-destructive">{deletePasswordError}</p>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => { setDeletePassword(''); setDeletePasswordError(''); }}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleDeleteAccount(); }}
                  disabled={isDeletingAccount || !deletePassword}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeletingAccount ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
